// eslint-disable-next-line import/no-nodejs-modules
import { ParsedUrlQuery } from 'querystring';
import classNames from 'classnames';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import Image from 'next/image';
import React from 'react';
import AgentAvatar from '../../components/agent/AgentAvatar';
import AgentCard from '../../components/agent/AgentCard';
import AgentLayoutRedesigned from '../../components/agent/AgentLayoutRedesigned';
// import ContactCard from '../../components/ContactCard';
import Seo from '../../components/Seo';
import AgentTestimonialsTextOnly from '../../components/testimonials/AgentTestimonialsTextOnly';
import ThemeProvider from '../../components/ThemeProvider';
import ContentfulService from '../../services/ContentfulService';
import { CommonPageDataProps } from '../../types';
import { RealApiAgentWebsiteResponse } from '../../types/real-api-types';
import {
  checkAvatarExists,
  getAgentWebsiteApiResponse,
} from '../../utils/agentWebsiteUtils';
import { capitalizeEnum, formatPhoneNumber } from '../../utils/StringUtils';
import BFFApiService from '../../services/BFFApiService';
import { OfficesResponse } from '../../openapi/bff';
import ErrorService from '../../services/ErrorService';

interface AboutPageProps extends CommonPageDataProps {
  agentWebsiteResponse: RealApiAgentWebsiteResponse;
  allOffices: OfficesResponse;
}

interface PageParams extends ParsedUrlQuery {
  agentSlug: string;
}

const AboutPage: NextPage<AboutPageProps> = ({
  agentWebsiteResponse,
  countriesWithStates,
  footerContent,
  allOffices,
}) => {
  const workWith = agentWebsiteResponse.agent.works_with
    .map((w) => capitalizeEnum(w))
    .join(', ');

  // TODO: Remove zipcodes once we are fully on wanderer and just use serviceAreaZipcodes.
  const serviceAreaZipcodes =
    agentWebsiteResponse.agent.serviceAreaZipcodes ||
    agentWebsiteResponse.agent.zipcodes;

  return (
    <ThemeProvider theme={agentWebsiteResponse.website.color_theme}>
      <AgentLayoutRedesigned
        agentWebsiteResponse={agentWebsiteResponse}
        countriesWithStates={countriesWithStates}
        footerContent={footerContent}
        showFooterCTA
        allOffices={allOffices}
      >
        <Seo
          title={`${agentWebsiteResponse.agent.name} - About`}
          description={agentWebsiteResponse.website.main_title}
          imageUrl='/img/default-socialimage.png'
        />
        <header className='relative flex justify-center items-center h-96 bg-dark '>
          <Image
            src='https://bolt-custom-assets.therealbrokerage.com/agent-website/images/themes/urban1_3x.jpg'
            alt='background'
            className='opacity-70 z-10'
            layout='fill'
            objectFit='cover'
          />
          <div className='w-full max-w-4xl h-auto mx-auto py-12 px-8 z-40'>
            <AgentAvatar
              imageUrl={checkAvatarExists(
                agentWebsiteResponse.agent.rect_profile_image
              )}
              name={agentWebsiteResponse.agent?.displayName}
              size='xl'
            />
            <h2 className='font-primary-medium text-4xl text-white py-4'>
              {agentWebsiteResponse.agent?.displayName}
            </h2>
          </div>
        </header>
        <div className='flex flex-col md:flex-row flex-wrap justify-center py-8'>
          <div className='flex flex-col flex-nowrap px-4'>
            <div className='pb-3'>
              <AgentCard>
                <h5 className='font-primary-medium text-xl text-dark'>
                  Contact Info:
                </h5>
                {agentWebsiteResponse.agent.phone_1_display && (
                  <p className='font-primary-light text-xl text-dark py-1'>
                    Mobile:{' '}
                    {formatPhoneNumber(
                      agentWebsiteResponse.agent.phone_1_display
                    )}
                  </p>
                )}
                <p className='font-primary-light text-xl text-dark py-1'>
                  Email: {agentWebsiteResponse.agent.email}
                </p>
              </AgentCard>
            </div>
            <div className='py-3 mb-4 md:mb-0'>
              <AgentCard>
                {workWith && (
                  <div>
                    <h5 className='font-primary-medium text-xl text-dark'>
                      I work with:
                    </h5>
                    <p className='font-primary-light text-xl text-dark'>
                      {workWith}
                    </p>
                  </div>
                )}

                <h5
                  className={classNames(
                    'font-primary-medium text-xl text-dark pb-2',
                    workWith ? 'pt-6' : 'pt-0'
                  )}
                >
                  Service area ZIP codes:
                </h5>
                {serviceAreaZipcodes ? (
                  <p className='font-primary-light text-xl text-dark break-all'>
                    {serviceAreaZipcodes}
                  </p>
                ) : (
                  <p className='font-primary-light text-xl text-dark break-all'>
                    N/A
                  </p>
                )}
              </AgentCard>
            </div>
          </div>
          {/* <div className='px-4 md:px-8'>
            <ContactCard agent={agentWebsiteResponse.agent} />
          </div> */}
        </div>
        {!!agentWebsiteResponse.website.testimonies?.length && (
          <div className='w-full max-w-7xl mx-auto px-6 lg:px-0 py-10 md:py-20'>
            <AgentTestimonialsTextOnly
              items={agentWebsiteResponse.website.testimonies}
              href={`${agentWebsiteResponse.agent.slug}/about`}
              limit={agentWebsiteResponse.website.testimonies?.length}
            />
          </div>
        )}
      </AgentLayoutRedesigned>
    </ThemeProvider>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  return { paths: [], fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps<
  AboutPageProps,
  PageParams
> = async (ctx) => {
  const contentfulService = new ContentfulService();
  const bffApiService = new BFFApiService();

  const [countriesWithStates, footerContent, allOffices] = await Promise.all([
    contentfulService.getCountriesWithStates(),
    contentfulService.getFooterContent(),
    bffApiService.fetchAllOfficialOffices(),
  ]);
  const agentWebsiteResponse: RealApiAgentWebsiteResponse =
    await getAgentWebsiteApiResponse(ctx.params.agentSlug);

  if (!agentWebsiteResponse) {
    return { notFound: true, revalidate: 1 };
  }

  if (!countriesWithStates) {
    const error = new Error('Unable to load agent website about page');
    ErrorService.notify('Unable to load agent website about page!', error, {
      apiCalls: {
        countriesWithStates: !!countriesWithStates,
      },
    });

    throw error;
  }

  return {
    props: {
      agentWebsiteResponse,
      countriesWithStates,
      footerContent,
      allOffices,
    },
    revalidate: 1,
  };
};

export default AboutPage;
